// Historial de ingestas recientes con procedencia (Tarea 19, Req. 20.3).
//
// Nota: el backend expone el estado por job (`GET /ingestion/jobs/{id}`); una
// lista general de historial se agregará cuando exista el endpoint de listado.
// Mientras tanto, esta vista permite consultar un job por id (procedencia:
// estado, propuesta, candidatas) y sirve de base para la vista de historial.

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useApi } from '../api/ApiContext.js';
import { AlertMessage, Badge, Card, Icon, PageHeader } from '../ui/components.js';
import type { IngestionJob } from '../types.js';

export function HistoryView() {
  const { t } = useTranslation();
  const api = useApi();
  const [jobId, setJobId] = useState('');
  const [job, setJob] = useState<IngestionJob | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function load(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setJob(null);
    try {
      const j = await api.ingestion.getJob(jobId.trim());
      setJob(j);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('history.errLoad'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex flex-col">
      <PageHeader
        title={t('history.pageTitle')}
        subtitle={t('history.pageSubtitle')}
        back
      />
      <div className="flex flex-col px-margin-mobile py-space-md gap-space-md">
      <Card title={t('history.cardTitle')} icon="history">
        <form onSubmit={load} className="flex flex-col gap-space-sm">
          <div className="flex flex-col gap-space-xs">
            <label className="field-label">{t('history.jobIdLabel')}</label>
            <div className="field-slot">
              <Icon name="fingerprint" className="text-on-surface-variant text-[18px] mr-space-xs" />
              <input className="field-input font-code-catalog" value={jobId} onChange={(e) => setJobId(e.target.value)} placeholder={t('history.jobIdPlaceholder')} />
            </div>
          </div>
          <button className="btn-primary w-full" type="submit" disabled={busy || !jobId.trim()}>
            <Icon name="travel_explore" className="text-[18px]" />
            {busy ? t('history.querying') : t('history.queryProvenance')}
          </button>
        </form>
      </Card>

      <AlertMessage message={error} />

      {job && (
        <Card title={t('history.jobTitle', { id: job.id.slice(0, 8) })} icon="receipt_long">
          <div className="flex items-center gap-space-xs">
            <span className="section-label text-on-surface-variant">{t('history.status')}</span>
            <Badge tone="info">{job.status}</Badge>
          </div>
          {job.image_s3_keys && job.image_s3_keys.length > 0 && (
            <p className="font-body-sm text-body-sm text-on-surface-variant mt-space-xs">
              {t('history.sourceImages', { count: job.image_s3_keys.length })}
            </p>
          )}
          {job.proposal && (
            <div className="mt-space-sm surface-rack-card !p-space-sm flex flex-col gap-space-xs">
              <span className="section-label text-vacuum-glow">{t('history.proposal')}</span>
              <p className="font-body-sm text-body-sm text-on-surface">{job.proposal.notes}</p>
              <p className="font-body-sm text-body-sm text-on-surface-variant">
                {job.proposal.proposal.artist} — {job.proposal.proposal.title}
                {job.proposal.proposal.edition_year ? ` (${job.proposal.proposal.edition_year})` : ''}
              </p>
              {job.proposal.duplicate_warning.is_possible_duplicate && (
                <div className="p-space-xs rounded bg-goldmine-vg/15 border border-goldmine-vg/40 text-goldmine-vg font-body-sm text-body-sm">
                  {job.proposal.duplicate_warning.reason}
                </div>
              )}
            </div>
          )}
          {job.accepted_record_id && (
            <p className="font-body-sm text-body-sm text-on-surface mt-space-sm">
              {t('history.acceptedAs')}{' '}
              <Link to={`/records/${job.accepted_record_id}`} className="text-primary underline font-code-catalog">
                {job.accepted_record_id.slice(0, 8)}
              </Link>
            </p>
          )}
          <AlertMessage message={job.error} className="mt-space-sm" />
        </Card>
      )}
      </div>
    </div>
  );
}
